<!-- Contact.svelte -->
<script>
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import Header from "lib/components/Header.svelte";
  import Footer from "lib/components/Footer.svelte";
  import Container from "lib/components/Container.svelte";
  import Button from "lib/components/Button.svelte";
  import { trackEvent } from "utils/analytics.js";
  import {
    initCachedBranch,
    onSubmitContactRequest,
  } from "lib/modules/Contact.svelte";
  import { PRIVACY_POLICY, TERMS_AND_CONDITIONS } from "utils/menu.js";

  // Form state
  let fullName = "";
  let email = "";
  let message = "";
  let selectedSource = "";
  let messageReceived = false;
  let isSubmitting = false;
  let formErrors = {};
  let showErrors = false;

  const sourceOptions = [
    { value: "affiliate", labelKey: "contact.form.sourceAffiliate" },
    { value: "google", labelKey: "contact.form.sourceGoogle" },
    { value: "social", labelKey: "contact.form.sourceSocial" },
    { value: "article", labelKey: "contact.form.sourceArticle" },
    { value: "friend", labelKey: "contact.form.sourceFriend" },
    { value: "other", labelKey: "contact.form.sourceOther" },
  ];

  function resetForm() {
    fullName = "";
    email = "";
    message = "";
    selectedSource = "";
    formErrors = {};
    showErrors = false;
  }

  function validateForm() {
    const errors = {};

    if (!fullName.trim()) {
      errors.fullName = $_("contact.form.error.fullNameRequired");
    }

    if (!email.trim()) {
      errors.email = $_("contact.form.error.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = $_("contact.form.error.emailInvalid");
    }

    if (!message.trim()) {
      errors.message = $_("contact.form.error.messageRequired");
    } else if (message.trim().length < 10) {
      errors.message = $_("contact.form.error.messageMinLength");
    }

    if (!selectedSource) {
      errors.selectedSource = $_("contact.form.error.sourceRequired");
    }

    return errors;
  }

  async function handleFormSubmit(event) {
    if (event) {
      event.preventDefault();
    }

    if (isSubmitting) return;

    // Validate form
    formErrors = validateForm();
    showErrors = true;

    if (Object.keys(formErrors).length > 0) {
      // Focus on first error field
      const firstErrorField = Object.keys(formErrors)[0];
      const fieldElement = document.getElementById(firstErrorField);
      if (fieldElement) {
        fieldElement.focus();
      }
      return;
    }

    // Trigger reCAPTCHA verification
    const submitButton = document.querySelector(".submit-button button");
    if (submitButton) {
      submitButton.click();
    }
  }

  async function handleSubmit(token) {
    if (isSubmitting) return;

    isSubmitting = true;

    try {
      const success = await onSubmitContactRequest({
        email,
        fullName,
        message: message.trim(),
        source: selectedSource,
        token,
      });

      if (success) {
        messageReceived = true;
        resetForm();

        // Track successful submission
        trackEvent("Contact", {
          action: "form_submit",
          source: selectedSource,
        });
      }
    } catch (error) {
      console.error("Contact form submission failed:", error);
      // Error handling is done in onSubmitContactRequest via toast
    } finally {
      isSubmitting = false;
    }
  }

  function handleInputChange(field) {
    return () => {
      if (showErrors && formErrors[field]) {
        // Clear error when user starts typing
        formErrors = { ...formErrors };
        delete formErrors[field];
      }
    };
  }

  onMount(() => {
    initCachedBranch();
    trackEvent("PageView", {
      page: "Contact",
    });
  });
</script>

<svelte:head>
  <title>Contact Us - Liquid Noble</title>
  <meta
    name="description"
    content="Get in touch with Liquid Noble. Have questions about our platform or need help? Contact us and we'll be in touch soon."
  />
</svelte:head>

<Header />

<Container variant="dark" size="large" class="contact-container">
  <div class="contact-header">
    <h1 class="contact-title">{$_("contact.title")}</h1>
    <p class="contact-subtitle">
      {$_("contact.subtitle")}
    </p>
  </div>

  <div class="contact-content">
    <div class="contact-form-section">
      <div class="form-container">
        {#if messageReceived}
          <div class="success-message" role="alert" aria-live="polite">
            <div class="success-icon">✓</div>
            <h3>{$_("contact.success.title")}</h3>
            <p>{$_("contact.success.message")}</p>
            <Button
              variant="secondary"
              size="medium"
              on:click={() => (messageReceived = false)}
            >
              {$_("contact.success.button")}
            </Button>
          </div>
        {:else}
          <form on:submit={handleFormSubmit} novalidate>
            <div class="form-group">
              <label for="fullName">{$_("contact.form.fullName")}</label>
              <input
                id="fullName"
                type="text"
                bind:value={fullName}
                on:input={handleInputChange("fullName")}
                placeholder={$_("contact.form.fullNamePlaceholder")}
                class:error={showErrors && formErrors.fullName}
                aria-describedby={showErrors && formErrors.fullName
                  ? "fullName-error"
                  : null}
                disabled={isSubmitting}
                required
              />
              {#if showErrors && formErrors.fullName}
                <span id="fullName-error" class="error-message" role="alert">
                  {formErrors.fullName}
                </span>
              {/if}
            </div>

            <div class="form-group">
              <label for="email">{$_("contact.form.email")}</label>
              <input
                id="email"
                type="email"
                bind:value={email}
                on:input={handleInputChange("email")}
                placeholder={$_("contact.form.emailPlaceholder")}
                class:error={showErrors && formErrors.email}
                aria-describedby={showErrors && formErrors.email
                  ? "email-error"
                  : null}
                disabled={isSubmitting}
                required
              />
              {#if showErrors && formErrors.email}
                <span id="email-error" class="error-message" role="alert">
                  {formErrors.email}
                </span>
              {/if}
            </div>

            <div class="form-group">
              <label for="message">{$_("contact.form.message")}</label>
              <textarea
                id="message"
                bind:value={message}
                on:input={handleInputChange("message")}
                placeholder={$_("contact.form.messagePlaceholder")}
                rows="5"
                class:error={showErrors && formErrors.message}
                aria-describedby={showErrors && formErrors.message
                  ? "message-error"
                  : null}
                disabled={isSubmitting}
                required
              ></textarea>
              {#if showErrors && formErrors.message}
                <span id="message-error" class="error-message" role="alert">
                  {formErrors.message}
                </span>
              {/if}
            </div>

            <div class="form-group">
              <label for="source">{$_("contact.form.source")}</label>
              <select
                id="selectedSource"
                bind:value={selectedSource}
                on:change={handleInputChange("selectedSource")}
                class:error={showErrors && formErrors.selectedSource}
                aria-describedby={showErrors && formErrors.selectedSource
                  ? "selectedSource-error"
                  : null}
                disabled={isSubmitting}
                required
              >
                <option value="" disabled
                  >{$_("contact.form.sourcePlaceholder")}</option
                >
                {#each sourceOptions as option}
                  <option value={option.value}>{$_(option.labelKey)}</option>
                {/each}
              </select>
              {#if showErrors && formErrors.selectedSource}
                <span
                  id="selectedSource-error"
                  class="error-message"
                  role="alert"
                >
                  {formErrors.selectedSource}
                </span>
              {/if}
            </div>

            <Button
              variant="primary"
              size="large"
              type="submit"
              reCAPTCHA={true}
              dataCallback={handleSubmit}
              disabled={isSubmitting}
              class="submit-button"
            >
              {isSubmitting
                ? $_("contact.form.submitting")
                : $_("contact.form.submit")}
            </Button>

            <!-- reCAPTCHA legal notice (required when badge is hidden) -->
            <div class="recaptcha-notice">
              {@html $_("contact.form.recaptchaNotice", {
                values: {
                  privacy: `<a href="${PRIVACY_POLICY}" target="_blank" rel="noopener">${$_("register.legalPrivacy")}</a>`,
                  terms: `<a href="${TERMS_AND_CONDITIONS}" target="_blank" rel="noopener">${$_("register.legalTerms")}</a>`,
                },
              })}
            </div>
          </form>
        {/if}
      </div>
    </div>

    <div class="contact-info-section">
      <div class="map-container">
        <img
          src="../../images/liquid_noble_map.svg"
          alt="Liquid Noble Location Map"
        />
      </div>

      <div class="contact-details">
        <div class="contact-item">
          <div class="contact-icon">📞</div>
          <div class="contact-info">
            <h4>{$_("contact.info.phone")}</h4>
            <a href="tel:+61734351507">{$_("contact.info.phoneNumber")}</a>
          </div>
        </div>

        <div class="contact-item">
          <div class="contact-icon">✉️</div>
          <div class="contact-info">
            <h4>{$_("contact.info.email")}</h4>
            <a href="mailto:hello@liquidnoble.com"
              >{$_("contact.info.emailAddress")}</a
            >
          </div>
        </div>

        <div class="contact-item">
          <div class="contact-icon">📍</div>
          <div class="contact-info">
            <h4>{$_("contact.info.address")}</h4>
            <a
              href="https://maps.app.goo.gl/SnfTYpitTRf5s4nt7"
              target="_blank"
              rel="noopener noreferrer"
            >
              {@html $_("contact.info.addressText").replace(/\\n/g, "<br>")}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</Container>

<Footer />

<style lang="scss">
  .contact-header {
    text-align: center;
    margin-bottom: 4rem;
  }

  .contact-title {
    font-size: 3.5rem;
    font-weight: 800;
    color: var(--text-color1);
    margin-bottom: 1.5rem;
    line-height: 1.1;
  }

  .contact-subtitle {
    font-size: 1.25rem;
    line-height: 1.6;
    color: var(--text-color2);
    max-width: 700px;
    margin: 0 auto;
  }

  .contact-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;
  }

  .contact-form-section {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    padding: 3rem;
  }

  .form-container {
    width: 100%;
  }

  .form-group {
    margin-bottom: 1.5rem;
  }

  .form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: var(--text-color1);
    font-size: 1rem;
  }

  .form-group input,
  .form-group textarea,
  .form-group select {
    width: 100%;
    padding: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-color2);
    font-size: 1rem;
    line-height: 1.5;
    transition: all 0.3s ease;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: var(--text-color1);
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 0 0 2px rgba(210, 165, 82, 0.2);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
      font-size: 1rem;
    }

    &.error {
      border-color: #ef4444;
      box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .form-group select {
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }

    /* Style the dropdown options */
    option {
      background: var(--background-color, #2a1a2e);
      color: var(--text-color2, #ffffff);
      padding: 0.5rem;
    }

    option:checked,
    option:focus {
      background: var(--text-color1) !important;
      color: var(--background-color, #1e0f30) !important;
    }

    /* Override browser default hover/focus styles */
    &:focus option:hover,
    &:focus option:checked {
      background: var(--text-color1) !important;
      color: var(--background-color, #1e0f30) !important;
    }

    /* Disabled option styling (placeholder) */
    option:disabled {
      color: rgba(255, 255, 255, 0.5) !important;
      background: var(--background-color, #1a0f1c) !important;
      font-style: italic;
    }
  }

  .form-group textarea {
    resize: vertical;
    min-height: 120px;
  }

  .error-message {
    display: block;
    color: #ef4444;
    font-size: 0.875rem;
    margin-top: 0.375rem;
    font-weight: 500;
    line-height: 1.4;
  }

  :global(.submit-button) {
    width: 100%;
    margin-top: 1rem;
  }

  .success-message {
    text-align: center;
    padding: 3rem 2rem;
  }

  .success-icon {
    width: 80px;
    height: 80px;
    background: var(--text-color1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
    color: var(--background-color, #1e0f30);
    margin: 0 auto 2rem;
    font-weight: bold;
  }

  .success-message h3 {
    color: var(--text-color1);
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  .success-message p {
    color: var(--text-color2);
    margin-bottom: 2rem;
  }

  .contact-info-section {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .map-container {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    padding: 2rem;
    text-align: center;
  }

  .map-container img {
    width: 100%;
    max-width: 400px;
    height: auto;
  }

  .contact-details {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .contact-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(210, 165, 82, 0.3);
    }
  }

  .contact-icon {
    font-size: 1.5rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      135deg,
      rgba(210, 165, 82, 0.3),
      rgba(219, 187, 107, 0.2)
    );
    border: 1px solid rgba(210, 165, 82, 0.3);
    border-radius: 8px;
    flex-shrink: 0;
  }

  .contact-info h4 {
    color: var(--text-color1);
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.25rem 0;
  }

  .contact-info a {
    color: var(--text-color2);
    text-decoration: none;
    font-size: 0.95rem;
    line-height: 1.4;
    transition: color 0.3s ease;

    @media (max-width: 480px) {
      font-size: 0.875rem; // 14px
      line-height: 1.3;
    }

    @media (max-width: 360px) {
      font-size: 0.8125rem; // 13px
      line-height: 1.2;
    }

    &:hover {
      color: var(--text-color1);
    }
  }

  /* reCAPTCHA legal notice */
  .recaptcha-notice {
    font-size: 0.875rem;
    color: var(--text-color2);
    margin-top: 0.75rem;
    line-height: 1.5;
    opacity: 0.9;

    @media (max-width: 768px) {
      font-size: 0.8125rem;
      line-height: 1.4;
    }

    @media (max-width: 480px) {
      font-size: 0.75rem;
      line-height: 1.3;
    }
  }

  .recaptcha-notice a {
    color: var(--text-color1);
    text-decoration: none;
    font-size: inherit;

    &:hover {
      text-decoration: underline;
    }
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .contact-content {
      grid-template-columns: 1fr;
      gap: 3rem;
    }

    .contact-form-section {
      padding: 2rem;
    }
  }

  @media (max-width: 768px) {
    .contact-title {
      font-size: 2.5rem;
    }

    .contact-subtitle {
      font-size: 1.1rem;
    }

    .contact-header {
      margin-bottom: 3rem;
    }

    .contact-content {
      gap: 2rem;
    }

    .contact-form-section {
      padding: 1.5rem;
    }

    .contact-item {
      padding: 1rem;
    }

    .contact-icon {
      font-size: 1.25rem;
      width: 36px;
      height: 36px;
    }
  }

  @media (max-width: 480px) {
    .contact-title {
      font-size: 2rem;
    }

    .contact-subtitle {
      font-size: 1rem;
    }

    .contact-form-section {
      padding: 1rem;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      padding: 0.75rem;
    }

    .success-message {
      padding: 2rem 1rem;
    }

    .success-icon {
      width: 60px;
      height: 60px;
      font-size: 2rem;
    }
  }
</style>
